import React, { useCallback, useRef } from 'react';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import SubscriptionForm from './SubscriptionForm';
import SubscriptionManageModal, { ManagedSubscription } from './SubscriptionManageModal';
import { renderDate, renderPriceDecimalColumn } from 'components/lists/functions/renderListColumn';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { useTranslation } from 'react-i18next';
import { useAllowedRight } from 'services/permissions/permissionChecks';

// Rename the name of the function:
const SubscriptionList: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { initializeModal } = useModal();

    // Only superusers may create subscriptions, staff users can only manage the running ones
    const isSuperuser = useAllowedRight('no_user');

    // Keep the refetch function of the list to refresh it after an action
    const refetchListRef = useRef<() => void>(() => {});

    // Specify the options of this list:
    const query = {
        endpoint: 'get_subscription_list',
        object: 'subscription'
    }
    const objectName = 'subscription.general.object_name'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = false

    // Specify form, detail modal or detailpage for this list
    const formModal = <SubscriptionForm />;

    // Open the manage modal of the clicked subscription
    const handleRowClick = (row: ManagedSubscription) => {
        initializeModal(
            <SubscriptionManageModal subscription={row} />, {
                title: t('subscription.manage.modal_title', { environmentName: row.environment_name }),
                modalSize: 'small-medium',
                onSuccess: () => refetchListRef.current(),
            }
        );
    };

    // Render the duration row, with the prepaid months when the period differs from the duration
    const renderDuration = (duration: string, prepaidMonths: number | null) => {
        const durationMonths = duration === 'yearly' ? 12 : 1;
        const showPrepaid = prepaidMonths !== null && prepaidMonths !== durationMonths;
        return (
            <>
                {t(`subscription.duration.${duration}`)}
                {showPrepaid && ` · ${t('subscription.duration.prepaid_months', { count: prepaidMonths })}`}
            </>
        );
    }

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { 
            width: '1.8fr', 
            field: 'environment_name', 
            label: 'subscription.general.environment_label',
            disableOrdering: true 
        },
        { 
            width: '0.9fr', 
            field: 'package_name', 
            label: 'subscription.general.package_label',
            disableOrdering: true 
        },
        { 
            width: '0.85fr', 
            field: 'start_date', 
            label: 'subscription.general.short_start_date_label',
            render: (_, row) => renderDate(row.start_date, userLocale),
            disableOrdering: true 
        },
        { 
            width: '0.85fr', 
            field: 'end_date', 
            label: 'subscription.general.short_end_date_label',
            render: (_, row) => renderDate(row.end_date, userLocale),
            disableOrdering: true  
        },
        { 
            width: '0.6fr', 
            field: 'duration', 
            label: 'subscription.general.duration_label',
            render: (_, row) => renderDuration(row.duration, row.prepaid_months ?? null),
            disableOrdering: true 
        },
        { 
            width: '0.75fr', 
            field: 'monthly_recurring_revenue', 
            label: 'subscription.general.mrr_label',
            render: (_, row) => renderPriceDecimalColumn(row.monthly_recurring_revenue, 'EUR', userLocale),
            disableOrdering: true 
        },
        { 
            width: '1.1fr', 
            field: 'status', 
            label: 'subscription.general.status_label',
            disableOrdering: true 
        },
    ];

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            showAddButton={isSuperuser}
            formModal={formModal}
            modalSize='small-medium'
            isPaginated={isPaginated}
            onRowClick={handleRowClick}
            setRefetchList={
                useCallback((fn: () => void) => {
                    refetchListRef.current = fn;
                }, [])
            }
        />
    );
};

export default SubscriptionList;
